import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { Input } from '../Input';
import { getBusinessTodayISODate } from '@/shared/time/businessTime';
import {
  formatBusinessDateLabel,
  generateCalendarDays,
  getMonthEndIso,
  getMonthLabel,
  getMonthStartIso,
  isDateDisabled,
  isMonthOutOfRange,
  isSameBusinessMonth,
  isYearOutOfRange,
  monthAbbrev,
  shiftMonthStart,
  weekdayLabels,
} from '@/shared/calendar/businessCalendar';

export interface SelectorFechaUnicaProps {
  /** Etiqueta mostrada arriba del campo, igual que en el `Input` de Contasis. */
  label?: string;
  /** Valor controlado en formato ISO (yyyy-mm-dd). `null` representa "vacío". */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Se dispara al cerrar el popover (selección, Escape o clic afuera). Útil para `touched` de React Hook Form. */
  onBlur?: () => void;
  /** Fecha mínima permitida (ISO). Días/meses/años anteriores quedan deshabilitados. */
  minDate?: string | null;
  /** Fecha máxima permitida (ISO). Días/meses/años posteriores quedan deshabilitados. */
  maxDate?: string | null;
  /** Si es true, no se muestra el botón "Limpiar" (el campo no puede quedar vacío una vez elegida una fecha). */
  required?: boolean;
  disabled?: boolean;
  /** Mensaje de error, igual que en el `Input` de Contasis (pinta el borde y lo muestra debajo). */
  error?: string;
  /** Oculta el texto del error (mantiene el borde rojo). Útil en espacios reducidos, como celdas de tabla. */
  hideErrorMessage?: boolean;
  placeholder?: string;
  formatDate?: (date: string) => string;
  size?: 'small' | 'medium' | 'large';
  /** Clases para el contenedor exterior del campo. */
  className?: string;
  /** Clases adicionales para el campo visible en sí (se suman a las propias del componente). */
  inputClassName?: string;
  id?: string;
  name?: string;
  /** Ícono a la izquierda del campo. Por defecto un calendario. */
  icon?: React.ReactNode;
}

type CalendarView = 'days' | 'months' | 'years';

const navBtnClass =
  'p-1 rounded transition-colors hover:bg-surface-hover disabled:opacity-40 disabled:pointer-events-none disabled:cursor-not-allowed';

const monthYearPillClass =
  'flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface-1 border border-muted text-sm font-semibold text-primary capitalize hover:bg-surface-hover hover:border-strong focus:outline-none focus:ring-2 focus:ring-brand transition-colors';

export const SelectorFechaUnica = React.forwardRef<HTMLInputElement, SelectorFechaUnicaProps>(({
  label,
  value,
  onChange,
  onBlur,
  minDate = null,
  maxDate = null,
  required = false,
  disabled = false,
  error,
  hideErrorMessage = false,
  placeholder = 'dd/mm/aaaa',
  formatDate,
  size = 'medium',
  className = '',
  inputClassName = '',
  id,
  name,
  icon,
}, forwardedInputRef) => {
  const todayIso = getBusinessTodayISODate();

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<CalendarView>('days');
  const [currentMonthStart, setCurrentMonthStart] = useState(() => getMonthStartIso(value ?? todayIso));
  const [yearsBlockStart, setYearsBlockStart] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [dropdownStyle, setDropdownStyle] = useState<{
    top: number;
    left?: number;
    right?: number;
    triggerTop: number;
    placement: 'below' | 'above';
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dayButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const setInputRef = useCallback((el: HTMLInputElement | null) => {
    inputRef.current = el;
    if (typeof forwardedInputRef === 'function') {
      forwardedInputRef(el);
    } else if (forwardedInputRef) {
      forwardedInputRef.current = el;
    }
  }, [forwardedInputRef]);

  const formatLabel = useCallback(
    (iso: string) => (formatDate ? formatDate(iso) : formatBusinessDateLabel(iso)),
    [formatDate],
  );

  // Si el mínimo/máximo cambia dinámicamente (p. ej. "Vencimiento" cuando cambia
  // "Emisión") y el valor actual ya no es válido, lo limpiamos.
  useEffect(() => {
    if (value && isDateDisabled(value, minDate, maxDate)) {
      onChange(null);
    }
    // 
  }, [minDate, maxDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideContainer && !insideDropdown && isOpen) {
        setIsOpen(false);
        setView('days');
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // 
  }, [isOpen]);

  // Ubica el popover debajo del campo por defecto. Se recalcula desde cero cada vez
  // que cambia la vista (días/meses/años) porque cada una tiene una altura distinta.
  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownStyle(null);
      return;
    }
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popoverWidth = 300;
      const horizontal = rect.left + popoverWidth <= window.innerWidth
        ? { left: rect.left }
        : { right: window.innerWidth - rect.right };
      setDropdownStyle({
        top: rect.bottom + 8,
        ...horizontal,
        triggerTop: rect.top,
        placement: 'below',
      });
    }
  }, [isOpen, view]);

  // Segunda pasada: si el popover ya renderizado se sale por abajo de la ventana,
  // lo volteamos para que se abra hacia arriba del campo en su lugar.
  useLayoutEffect(() => {
    if (!isOpen || !dropdownStyle || dropdownStyle.placement === 'above' || !dropdownRef.current) {
      return;
    }
    const popoverRect = dropdownRef.current.getBoundingClientRect();
    if (popoverRect.bottom > window.innerHeight) {
      setDropdownStyle((prev) => prev && ({
        ...prev,
        top: Math.max(8, prev.triggerTop - popoverRect.height - 8),
        placement: 'above',
      }));
    }
  }, [isOpen, dropdownStyle]);

  useEffect(() => {
    if (isOpen && view === 'days') {
      dayButtonRefs.current[focusedIndex]?.focus();
    }
    // 
  }, [isOpen]);

  const calendarDays = useMemo(() => generateCalendarDays(currentMonthStart), [currentMonthStart]);

  const prevMonthDisabled = useMemo(() => {
    const prevStart = shiftMonthStart(currentMonthStart, -1);
    return !!minDate && getMonthEndIso(prevStart) < minDate;
  }, [currentMonthStart, minDate]);

  const nextMonthDisabled = useMemo(() => {
    const nextStart = shiftMonthStart(currentMonthStart, 1);
    return !!maxDate && nextStart > maxDate;
  }, [currentMonthStart, maxDate]);

  const todayDisabled = isDateDisabled(todayIso, minDate, maxDate);

  const openPicker = () => {
    if (disabled) return;
    const baseMonth = getMonthStartIso(value ?? todayIso);
    setCurrentMonthStart(baseMonth);
    setView('days');
    const days = generateCalendarDays(baseMonth);
    const targetIso = value ?? todayIso;
    const idx = days.indexOf(targetIso);
    setFocusedIndex(idx >= 0 ? idx : 0);
    setIsOpen(true);
  };

  const closePicker = (returnFocus: boolean) => {
    setIsOpen(false);
    setView('days');
    onBlur?.();
    if (returnFocus) inputRef.current?.focus();
  };

  const selectDate = (iso: string) => {
    if (isDateDisabled(iso, minDate, maxDate)) return;
    onChange(iso);
    closePicker(true);
  };

  const clearDate = () => {
    onChange(null);
    closePicker(true);
  };

  const handleDayKeyDown = (event: React.KeyboardEvent, index: number) => {
    const moves: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 };
    if (moves[event.key] !== undefined) {
      event.preventDefault();
      let next = index;
      const step = moves[event.key];
      do {
        next += step;
      } while (next >= 0 && next < calendarDays.length && isDateDisabled(calendarDays[next], minDate, maxDate));
      if (next >= 0 && next < calendarDays.length) {
        setFocusedIndex(next);
        dayButtonRefs.current[next]?.focus();
      }
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectDate(calendarDays[index]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closePicker(true);
    }
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) openPicker();
    } else if (event.key === 'Escape' && isOpen) {
      closePicker(false);
    }
  };

  const renderDaysView = () => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          aria-label="Mes anterior"
          disabled={prevMonthDisabled}
          onClick={() => setCurrentMonthStart((prev) => shiftMonthStart(prev, -1))}
          className={navBtnClass}
        >
          <ChevronLeft className="w-4 h-4 text-secondary" />
        </button>

        <button type="button" onClick={() => setView('months')} className={monthYearPillClass} aria-label="Cambiar mes y año">
          {getMonthLabel(currentMonthStart)}
          <ChevronDown className="w-3.5 h-3.5 text-tertiary" />
        </button>

        <button
          type="button"
          aria-label="Mes siguiente"
          disabled={nextMonthDisabled}
          onClick={() => setCurrentMonthStart((prev) => shiftMonthStart(prev, 1))}
          className={navBtnClass}
        >
          <ChevronRight className="w-4 h-4 text-secondary" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdayLabels.map((day, i) => (
          <div key={i} className="text-center text-xs font-medium text-muted py-1">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((dayIso, index) => {
          const isCurrentMonth = isSameBusinessMonth(dayIso, currentMonthStart);
          const isToday = dayIso === todayIso;
          const isSelected = dayIso === value;
          const isOutOfRange = isDateDisabled(dayIso, minDate, maxDate);
          const isDisabledDay = isOutOfRange;
          const dayNumber = Number(dayIso.slice(8, 10));

          const dayClasses = ['w-8', 'h-8', 'text-sm', 'rounded-md', 'transition-colors'];
          if (isOutOfRange) {
            dayClasses.push('text-disabled', 'cursor-not-allowed');
          } else if (isSelected) {
            dayClasses.push('bg-brand', 'text-inverse', 'hover:bg-brand-hover', 'font-semibold');
          } else if (!isCurrentMonth) {
            dayClasses.push('text-disabled', 'hover:bg-surface-hover', 'hover:text-secondary');
          } else {
            dayClasses.push('text-secondary', 'hover:bg-surface-hover');
            if (isToday) dayClasses.push('font-bold', 'text-brand');
          }

          return (
            <button
              key={dayIso}
              ref={(el) => {
                dayButtonRefs.current[index] = el;
              }}
              type="button"
              tabIndex={index === focusedIndex ? 0 : -1}
              disabled={isDisabledDay}
              aria-label={dayIso}
              aria-pressed={isSelected}
              aria-current={isToday ? 'date' : undefined}
              onClick={() => selectDate(dayIso)}
              onFocus={() => setFocusedIndex(index)}
              onKeyDown={(e) => handleDayKeyDown(e, index)}
              className={dayClasses.join(' ')}
            >
              {dayNumber}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-muted">
        <button
          type="button"
          onClick={() => selectDate(todayIso)}
          disabled={todayDisabled}
          className="px-2 py-1 text-xs font-medium text-brand hover:underline disabled:text-disabled disabled:no-underline disabled:cursor-not-allowed"
        >
          Hoy
        </button>
        {!required && (
          <button
            type="button"
            onClick={clearDate}
            className="px-2 py-1 text-xs font-medium text-tertiary hover:text-secondary transition-colors"
          >
            Limpiar
          </button>
        )}
      </div>
    </div>
  );

  const renderMonthsView = () => {
    const year = Number(currentMonthStart.slice(0, 4));
    const currentMonthIndex = Number(currentMonthStart.slice(5, 7)) - 1;
    const todayYear = Number(todayIso.slice(0, 4));
    const todayMonthIndex = Number(todayIso.slice(5, 7)) - 1;
    const prevYearDisabled = isYearOutOfRange(year - 1, minDate, maxDate);
    const nextYearDisabled = isYearOutOfRange(year + 1, minDate, maxDate);

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            aria-label="Año anterior"
            disabled={prevYearDisabled}
            onClick={() => setCurrentMonthStart((prev) => `${year - 1}${prev.slice(4)}`)}
            className={navBtnClass}
          >
            <ChevronLeft className="w-4 h-4 text-secondary" />
          </button>

          <button
            type="button"
            onClick={() => {
              setYearsBlockStart(year - (year % 12));
              setView('years');
            }}
            className={monthYearPillClass}
            aria-label="Cambiar año"
          >
            {year}
            <ChevronDown className="w-3.5 h-3.5 text-tertiary" />
          </button>

          <button
            type="button"
            aria-label="Año siguiente"
            disabled={nextYearDisabled}
            onClick={() => setCurrentMonthStart((prev) => `${year + 1}${prev.slice(4)}`)}
            className={navBtnClass}
          >
            <ChevronRight className="w-4 h-4 text-secondary" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1">
          {monthAbbrev.map((abbrev, index) => {
            const isDisabledMonth = isMonthOutOfRange(year, index, minDate, maxDate);
            const isCurrent = index === currentMonthIndex || (year === todayYear && index === todayMonthIndex);
            return (
              <button
                key={abbrev}
                type="button"
                disabled={isDisabledMonth}
                onClick={() => {
                  setCurrentMonthStart(`${year}-${String(index + 1).padStart(2, '0')}-01`);
                  setView('days');
                }}
                className={[
                  'py-2.5 text-sm rounded-md capitalize transition-colors disabled:pointer-events-none',
                  isDisabledMonth
                    ? 'text-disabled cursor-not-allowed'
                    : isCurrent
                    ? 'text-brand font-semibold hover:bg-surface-hover'
                    : 'text-secondary hover:bg-surface-hover',
                ].join(' ')}
              >
                {abbrev}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderYearsView = () => {
    const currentYear = Number(currentMonthStart.slice(0, 4));
    const blockStart = yearsBlockStart ?? currentYear - (currentYear % 12);
    const todayYear = Number(todayIso.slice(0, 4));
    const prevBlockEnd = `${blockStart - 1}-12-31`;
    const nextBlockStart = `${blockStart + 12}-01-01`;
    const prevBlockDisabled = !!minDate && prevBlockEnd < minDate;
    const nextBlockDisabled = !!maxDate && nextBlockStart > maxDate;
    const years = Array.from({ length: 12 }, (_, i) => blockStart + i);

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            aria-label="Años anteriores"
            disabled={prevBlockDisabled}
            onClick={() => setYearsBlockStart(blockStart - 12)}
            className={navBtnClass}
          >
            <ChevronLeft className="w-4 h-4 text-secondary" />
          </button>
          <span className="text-sm font-semibold text-primary">
            {blockStart} - {blockStart + 11}
          </span>
          <button
            type="button"
            aria-label="Años siguientes"
            disabled={nextBlockDisabled}
            onClick={() => setYearsBlockStart(blockStart + 12)}
            className={navBtnClass}
          >
            <ChevronRight className="w-4 h-4 text-secondary" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1">
          {years.map((y) => {
            const isDisabledYear = isYearOutOfRange(y, minDate, maxDate);
            const isCurrent = y === currentYear || y === todayYear;
            return (
              <button
                key={y}
                type="button"
                disabled={isDisabledYear}
                onClick={() => {
                  setCurrentMonthStart(`${y}${currentMonthStart.slice(4)}`);
                  setView('months');
                }}
                className={[
                  'py-2.5 text-sm rounded-md transition-colors disabled:pointer-events-none',
                  isDisabledYear
                    ? 'text-disabled cursor-not-allowed'
                    : isCurrent
                    ? 'text-brand font-semibold hover:bg-surface-hover'
                    : 'text-secondary hover:bg-surface-hover',
                ].join(' ')}
              >
                {y}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const displayValue = value ? formatLabel(value) : '';

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      <Input
        ref={setInputRef}
        id={id}
        name={name}
        label={label}
        required={required}
        error={error}
        hideErrorMessage={hideErrorMessage}
        size={size}
        disabled={disabled}
        readOnly
        leftIcon={icon ?? <Calendar className="w-4 h-4" />}
        value={displayValue}
        placeholder={placeholder}
        onClick={() => {
          if (!isOpen) openPicker();
        }}
        onKeyDown={handleTriggerKeyDown}
        className={`cursor-pointer caret-transparent ${inputClassName}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      />

      {isOpen && dropdownStyle && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          role="dialog"
          style={{ top: dropdownStyle.top, left: dropdownStyle.left, right: dropdownStyle.right }}
          className="fixed bg-surface-0 rounded-xl shadow-xl border border-default z-[9999] p-3 w-[300px]"
        >
          {view === 'days' && renderDaysView()}
          {view === 'months' && renderMonthsView()}
          {view === 'years' && renderYearsView()}
        </div>,
        document.body,
      )}
    </div>
  );
});

SelectorFechaUnica.displayName = 'SelectorFechaUnica';

export default SelectorFechaUnica;
